/**
 * Event Tracker Atom
 *
 * Manages a singleton EventTracker instance via Jotai. The tracker is
 * created lazily only when Voice Notes is enabled (client sets
 * eventTrackerEnabledAtom to true). When disabled, a no-op tracker is
 * returned so callers need no null checks.
 *
 * Usage from action atoms:
 *   const tracker = get(eventTrackerAtom);
 *   tracker.track('recording_started');
 *
 * Usage from React components:
 *   const tracker = useAtomValue(eventTrackerAtom);
 *   tracker.track('template_selected', { template_id: id });
 */
import { atom } from 'jotai';

// Root package version (set by CICD) for event tracker clientVersion
// eslint-disable-next-line import/extensions -- JSON import for root package version
import rootPackageJson from '../../../../package.json';
import {
  type EventTracker,
  createEventTracker,
  noopEventTracker,
} from '../upload/eventTracker';

import { ambientDepsAtom } from './synapseUpload';

/**
 * When true, the real event tracker is created and used. Set by the client
 * when preferences indicate Voice Notes is enabled (e.g. isVideaNotesEnabled
 * or enableVoiceNotesStandalone). Default false so we don't create the tracker
 * or send app_initialized for users without Voice Notes.
 */
export const eventTrackerEnabledAtom = atom(false);

/** Lazy singleton: created on first read of eventTrackerAtom when enabled. */
let trackerInstance: EventTracker | null = null;

/**
 * Returns the real tracker when Voice Notes is enabled (lazy-created once),
 * otherwise the no-op tracker. Keeps deps in sync with ambientDepsAtom when
 * the real tracker exists.
 */
export const eventTrackerAtom = atom((get): EventTracker => {
  const enabled = get(eventTrackerEnabledAtom);

  if (!enabled) {
    return noopEventTracker;
  }

  if (trackerInstance === null) {
    trackerInstance = createEventTracker({
      clientVersion: rootPackageJson.version,
    });
  }

  const deps = get(ambientDepsAtom);
  if (deps) {
    trackerInstance.updateDeps(deps);
  }

  return trackerInstance;
});
